import { AfterContentChecked, Directive, ElementRef, HostListener, Input, NgZone, OnChanges, OnDestroy } from '@angular/core';
import { fromEvent, ReplaySubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

const MAX_LOOKUP_RETRIES = 3;

@Directive({
  // tslint:disable-next-line: directive-selector
  selector: 'textarea[autosize]',
  // tslint:disable-next-line: use-host-property-decorator
  host: {
    style: 'overflow: hidden'
  }
})
export class AutosizeDirective
  implements OnDestroy, OnChanges, AfterContentChecked {
  @Input() minRows: number;
  @Input() maxRows: number;
  @Input() onlyGrow = false;
  @Input() useImportant = false;

  private retries = 0;
  private textAreaEl: any;

  private oldContent: string;
  private oldWidth: number;

  private destroyed$ = new ReplaySubject(1);

  @HostListener('input', ['$event.target'])
  onInput(textArea: HTMLTextAreaElement): void {
    this.adjust();
  }

  constructor(public element: ElementRef, private zone: NgZone) {
    if (this.element.nativeElement.tagName !== 'TEXTAREA') {
      this._findNestedTextArea();
    } else {
      this.textAreaEl = this.element.nativeElement;
      this.textAreaEl.style.overflow = 'hidden';
      this._onTextAreaFound();
    }
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngAfterContentChecked() {
    this.adjust();
  }

  ngOnChanges(changes) {
    this.adjust(true);
  }

  _findNestedTextArea() {
    this.textAreaEl = this.element.nativeElement.querySelector('TEXTAREA');

    if (!this.textAreaEl && this.element.nativeElement.shadowRoot) {
      this.textAreaEl = this.element.nativeElement.shadowRoot.querySelector(
        'TEXTAREA'
      );
    }

    if (!this.textAreaEl) {
      if (this.retries > MAX_LOOKUP_RETRIES) {
        this.retries++;
        setTimeout(() => {
          this._findNestedTextArea();
        }, 100);
      }
      return;
    }

    this.textAreaEl.style.overflow = 'hidden';
    this._onTextAreaFound();
  }

  _onTextAreaFound() {
    this.zone.runOutsideAngular(() => {
      fromEvent(window, 'resize')
        .pipe(
          takeUntil(this.destroyed$),
          debounceTime(200),
          distinctUntilChanged()
        )
        .subscribe(() => {
          this.zone.run(() => {
            this.adjust();
          });
        });
    });
    setTimeout(() => {
      this.adjust();
    });
  }

  adjust(inputsChanged = false): void {
    if (this.textAreaEl) {
      const currentText = this.textAreaEl.value;

      if (
        inputsChanged === false &&
        currentText === this.oldContent &&
        this.textAreaEl.offsetWidth === this.oldWidth
      ) {
        return;
      }

      this.oldContent = currentText;
      this.oldWidth = this.textAreaEl.offsetWidth;
      const clone = this.textAreaEl.cloneNode(true);
      const parent = this.textAreaEl.parentNode;
      clone.style.visibility = 'hidden';
      parent.appendChild(clone);

      clone.style.overflow = 'auto';
      clone.style.height = 'auto';

      let height = clone.scrollHeight;

      // add into height top and bottom borders' width
      const computedStyle = window.getComputedStyle(clone, null);
      // tslint:disable-next-line: radix
      height += parseInt(computedStyle.getPropertyValue('border-top-width'));
      // tslint:disable-next-line: radix
      height += parseInt(computedStyle.getPropertyValue('border-bottom-width'));

      const willGrow = height > this.textAreaEl.offsetHeight;

      if (this.onlyGrow === false || willGrow) {
        const lineHeight = this._getLineHeight();
        const rowsCount = height / lineHeight;

        let styleAttribute = '';

        if (this.minRows && this.minRows >= rowsCount) {
          height = this.minRows * lineHeight;
        } else if (this.maxRows && this.maxRows <= rowsCount) {
          height = this.maxRows * lineHeight;
          styleAttribute += 'overflow: auto;';
        } else {
          styleAttribute += 'overflow: hidden;';
        }

        styleAttribute += `height: ${height}px`;

        styleAttribute += this.useImportant ? '!important;' : ';';

        this.textAreaEl.setAttribute('style', styleAttribute);
      }

      parent.removeChild(clone);
    }
  }

  private _getLineHeight() {
    let lineHeight = parseInt(this.textAreaEl.style.lineHeight, 10);
    if (isNaN(lineHeight) && window.getComputedStyle) {
      const styles = window.getComputedStyle(this.textAreaEl);
      lineHeight = parseInt(styles.lineHeight, 10);
    }

    if (isNaN(lineHeight)) {
      const fontSize = window
        .getComputedStyle(this.textAreaEl, null)
        .getPropertyValue('font-size');
      lineHeight = Math.floor(parseInt(fontSize.replace('px', ''), 10) * 1.5);
    }

    return lineHeight;
  }
}
